import { create } from 'zustand';
import {
  DEFAULT_SETTINGS,
  type ArbiterEvent,
  type CaseDocumentMeta,
  type CaseInfo,
  type ChatMessage,
  type CompiledContext,
  type KoboldStatus,
  type MetricsSummary,
  type ModelProfile,
  type SchedulerSnapshot,
  type SessionInfo,
  type SessionState,
  type Settings,
  type TurnTimeline,
} from '@voicer/shared';

export type Tab = 'chat' | 'dashboard' | 'context' | 'settings';

export type ArbiterState = 'idle' | 'listening' | 'thinking' | 'speaking' | 'candidate' | 'ducked' | 'paused' | 'classifying';

export interface LogLine {
  ts: number;
  level: 'info' | 'warn' | 'error';
  msg: string;
}

export interface AudioTelemetry {
  micLevel: number;
  vadProb: number;
  noiseFloor: number;
  echoCorr: number;
  echoChronic: boolean;
  baseLatencyMs: number;
  outputLatencyMs: number;
  micReady: boolean;
  micError: string | null;
  pttHeld: boolean;
}

interface StoreState {
  connected: boolean;
  tab: Tab;
  settings: Settings;
  profiles: ModelProfile[];
  status: KoboldStatus | null;
  sessions: SessionInfo[];
  session: SessionState | null;
  cases: CaseInfo[];
  activeCaseId: string | null;
  caseDocuments: CaseDocumentMeta[];
  selectedDocumentId: string | null;
  streaming: { turnId: string; text: string } | null;
  compiled: CompiledContext | null;
  dryRun: CompiledContext | null;
  turns: TurnTimeline[];
  summary: MetricsSummary | null;
  arbiterEvents: ArbiterEvent[];
  scheduler: SchedulerSnapshot | null;
  background: { job: string; state: string; detail?: string; ts: number }[];
  logs: LogLine[];
  arbiterState: ArbiterState;
  audio: AudioTelemetry;
  pendingUtterance: string;

  setTab: (t: Tab) => void;
  setConnected: (c: boolean) => void;
  setSettings: (s: Settings) => void;
  setProfiles: (p: ModelProfile[]) => void;
  setStatus: (s: KoboldStatus) => void;
  setSessions: (s: SessionInfo[]) => void;
  setSession: (s: SessionState) => void;
  setCases: (c: CaseInfo[]) => void;
  setActiveCaseId: (id: string | null) => void;
  setCaseDocuments: (d: CaseDocumentMeta[]) => void;
  setSelectedDocumentId: (id: string | null) => void;
  upsertMessage: (m: ChatMessage) => void;
  deleteMessage: (id: string) => void;
  setStreaming: (s: { turnId: string; text: string } | null) => void;
  setCompiled: (c: CompiledContext) => void;
  setDryRun: (c: CompiledContext | null) => void;
  addTurn: (t: TurnTimeline) => void;
  setTurns: (t: TurnTimeline[]) => void;
  setSummary: (s: MetricsSummary) => void;
  upsertArbiterEvent: (e: ArbiterEvent) => void;
  setArbiterEvents: (e: ArbiterEvent[]) => void;
  setScheduler: (s: SchedulerSnapshot) => void;
  addBackground: (b: { job: string; state: string; detail?: string }) => void;
  log: (level: LogLine['level'], msg: string) => void;
  setArbiterState: (s: ArbiterState) => void;
  setAudio: (patch: Partial<AudioTelemetry>) => void;
  setPendingUtterance: (t: string) => void;
}

export const useStore = create<StoreState>((set) => ({
  connected: false,
  tab: 'chat',
  settings: DEFAULT_SETTINGS,
  profiles: [],
  status: null,
  sessions: [],
  session: null,
  cases: [],
  activeCaseId: null,
  caseDocuments: [],
  selectedDocumentId: null,
  streaming: null,
  compiled: null,
  dryRun: null,
  turns: [],
  summary: null,
  arbiterEvents: [],
  scheduler: null,
  background: [],
  logs: [],
  arbiterState: 'idle',
  audio: {
    micLevel: 0,
    vadProb: 0,
    noiseFloor: 0,
    echoCorr: 0,
    echoChronic: false,
    baseLatencyMs: 0,
    outputLatencyMs: 0,
    micReady: false,
    micError: null,
    pttHeld: false,
  },
  pendingUtterance: '',

  setTab: (tab) => set({ tab }),
  setConnected: (connected) => set({ connected }),
  setSettings: (settings) => set({ settings }),
  setProfiles: (profiles) => set({ profiles }),
  setStatus: (status) => set({ status }),
  setSessions: (sessions) => set({ sessions }),
  setSession: (session) => set({ session }),
  setCases: (cases) => set({ cases }),
  setActiveCaseId: (activeCaseId) => set({ activeCaseId }),
  setCaseDocuments: (caseDocuments) => set({ caseDocuments }),
  setSelectedDocumentId: (selectedDocumentId) => set({ selectedDocumentId }),
  upsertMessage: (m) =>
    set((s) => {
      if (!s.session || s.session.session.id !== m.sessionId) return {};
      const msgs = [...s.session.messages];
      const i = msgs.findIndex((x) => x.id === m.id);
      if (i >= 0) {
        msgs[i] = m;
        return { session: { ...s.session, messages: msgs } };
      }
      msgs.push(m);
      msgs.sort((a, b) => a.seq - b.seq);
      // Spine messages advance the tip locally until the next full session push.
      const advanceLeaf = m.role !== 'aside';
      return {
        session: {
          ...s.session,
          messages: msgs,
          activeLeafId: advanceLeaf ? m.id : s.session.activeLeafId,
          forwardLeafId: advanceLeaf ? null : s.session.forwardLeafId,
        },
      };
    }),
  deleteMessage: (id) =>
    set((s) => {
      if (!s.session) return {};
      const cur = s.session.messages.find((m) => m.id === id);
      const messages = s.session.messages.filter((m) => m.id !== id);
      let activeLeafId = s.session.activeLeafId;
      let forwardLeafId = s.session.forwardLeafId;
      if (activeLeafId === id) activeLeafId = cur?.parentId ?? null;
      if (forwardLeafId === id) forwardLeafId = null;
      return { session: { ...s.session, messages, activeLeafId, forwardLeafId } };
    }),
  setStreaming: (streaming) => set({ streaming }),
  setCompiled: (compiled) => set({ compiled }),
  setDryRun: (dryRun) => set({ dryRun }),
  addTurn: (t) =>
    set((s) => {
      const turns = s.turns.filter((x) => x.turnId !== t.turnId);
      turns.push(t);
      return { turns: turns.slice(-300) };
    }),
  setTurns: (turns) => set({ turns }),
  setSummary: (summary) => set({ summary }),
  upsertArbiterEvent: (e) =>
    set((s) => {
      const list = s.arbiterEvents.filter((x) => x.id !== e.id);
      list.push(e);
      list.sort((a, b) => a.ts - b.ts);
      return { arbiterEvents: list.slice(-500) };
    }),
  setArbiterEvents: (arbiterEvents) => set({ arbiterEvents }),
  setScheduler: (scheduler) => set({ scheduler }),
  addBackground: (b) => set((s) => ({ background: [...s.background.slice(-19), { ...b, ts: Date.now() }] })),
  log: (level, msg) => set((s) => ({ logs: [...s.logs.slice(-199), { ts: Date.now(), level, msg }] })),
  setArbiterState: (arbiterState) => set({ arbiterState }),
  setAudio: (patch) => set((s) => ({ audio: { ...s.audio, ...patch } })),
  setPendingUtterance: (pendingUtterance) => set({ pendingUtterance }),
}));
